/**
 * InlineEmptyState Component
 *
 * Reusable empty state display for section-level empty states within forms and detail pages.
 * Used to indicate no items have been added yet with optional call-to-action.
 *
 * Features:
 * - Optional icon or emoji display
 * - Optional heading text
 * - Message text (required)
 * - Optional action button
 * - Flexible styling via className
 * - Consistent empty state patterns
 */

import { LucideIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export interface InlineEmptyStateProps {
  /**
   * Optional Lucide icon component to display
   */
  icon?: LucideIcon

  /**
   * Optional emoji to display (alternative to icon)
   */
  emoji?: string

  /**
   * Optional heading text
   */
  heading?: string

  /**
   * Main message text (required)
   */
  message: string

  /**
   * Optional secondary message
   */
  secondaryMessage?: string

  /**
   * Optional action button label
   */
  actionLabel?: string

  /**
   * Optional action button icon
   */
  actionIcon?: LucideIcon

  /**
   * Callback when action button is clicked
   */
  onAction?: () => void

  /**
   * Button variant
   * @default 'default'
   */
  buttonVariant?: 'default' | 'outline'

  /**
   * Optional additional CSS classes for container
   */
  className?: string
}

/**
 * Displays an empty state message with optional icon and action button
 */
export function InlineEmptyState({
  icon: Icon,
  emoji,
  heading,
  message,
  secondaryMessage,
  actionLabel,
  actionIcon: ActionIcon,
  onAction,
  buttonVariant = 'default',
  className,
}: InlineEmptyStateProps) {
  return (
    <div className={cn('py-12 text-center', className)}>
      {/* Icon or Emoji */}
      {Icon && (
        <Icon
          className="mx-auto mb-4 h-12 w-12 text-gray-300"
          aria-hidden="true"
        />
      )}
      {emoji && !Icon && (
        <div className="mb-3 text-4xl" aria-hidden="true">
          {emoji}
        </div>
      )}

      {/* Heading */}
      {heading && (
        <h3 className="mb-2 text-base font-medium text-gray-900">{heading}</h3>
      )}

      {/* Message */}
      <p className="mb-4 text-sm text-gray-500">{message}</p>

      {/* Secondary Message */}
      {secondaryMessage && (
        <p className="mt-1 text-xs text-gray-500">{secondaryMessage}</p>
      )}

      {/* Action Button */}
      {actionLabel && onAction && (
        <Button
          type="button"
          onClick={onAction}
          variant={buttonVariant}
          className={cn(
            buttonVariant === 'outline' &&
              'border-[#0033A0] text-[#0033A0] hover:bg-[#0033A0]/10'
          )}
        >
          {ActionIcon && <ActionIcon className="mr-2 h-4 w-4" />}
          {actionLabel}
        </Button>
      )}
    </div>
  )
}
